import { Link } from 'react-router-dom';
import { SEO } from '../../utils/seo';
import FinancingBanner from '../../components/feature/FinancingBanner';

export default function ServicesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Agricultural Drone Services",
    "provider": {
      "@type": "Organization",
      "name": "Greenfield Partners"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "description": "Precision aerial application, yield protection services, land management, and field intelligence for row-crop farmers"
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Agricultural Drone Services - Precision Aerial Application | Greenfield Partners"
        description="From precision application to land management and field intelligence, every service is designed to protect yield, save time, and increase return per acre."
        keywords="agricultural drone services, precision aerial application, crop spraying, deer deterrent, animal recovery, field surveys, NDVI mapping"
        schema={schema}
      />
      {/* Hero Section */}
      <section className="relative py-32 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Services Built Around Your Operation
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            From precision application to land management and field intelligence, every service is designed to protect yield, save time, and increase return per acre.
          </p>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Core Services */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center">Core Services</h2>

        <div className="space-y-16">
          {/* Precision Aerial Application */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-plant-line text-6xl text-emerald-800"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4">Precision Aerial Application</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Apply fungicides, nutrients, and crop protection products without driving through the field. Zero wheel traffic means no compaction, no broken stalks, and no stand loss.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                When fields are too wet or crops are too tall, we can still get the job done on time. Weather doesn't wait, and neither should you.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Fungicide application during critical growth stages</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Foliar nutrient application when timing matters</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Application in wet conditions or tall crops</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Up to 80% reduction in crop damage vs. ground equipment</span>
                </li>
              </ul>
            </div>
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/a191e5a1678d824b58a7ef08dc5fb946.jpg"
                alt="Precision aerial application"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>

          {/* Yield Protection Services */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=Edge%20of%20cornfield%20bordering%20woodland%20area%20showing%20healthy%20crop%20protection%20from%20deer%20damage%2C%20clear%20boundary%20between%20forest%20and%20farmland%2C%20mature%20corn%20stalks%20standing%20strong%20near%20tree%20line%2C%20natural%20daylight%20with%20earth%20tones%20and%20deep%20greens&width=800&height=900&seq=deer001&orientation=portrait"
                alt="Field edge protection"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-6xl text-emerald-800"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4">Yield Protection Services</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Deer damage, invasive species, and overgrowth can cost you bushels from field edge to field edge. We help you protect what you've worked all season to grow.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Every acre matters. These services are about protecting your investment and maximizing return on every part of your operation.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Deer damage mitigation and population management</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Invasive species control at field edges</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Overgrowth management for better field access</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>10–20% yield protection in high-pressure areas</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Residential & Specialty Crop Deer Deterrent */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-6xl text-emerald-800"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4">Residential & Specialty Crop Deer Deterrent</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Protect your landscaping, gardens, and specialty crops from costly deer damage. Our aerial deterrent application covers more ground faster and more effectively than traditional methods.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Perfect for homeowners, Christmas tree farms, nurseries, and orchards dealing with persistent deer pressure.
              </p>
              <ul className="space-y-3 text-gray-700 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>85% reduction in deer browse damage</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>4–6 weeks of protection per application</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Safe for plants, pets, and people</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Cost-effective at just $15/acre</span>
                </li>
              </ul>
              <a
                href="/services/deer-deterrent"
                className="inline-flex items-center text-emerald-800 font-semibold hover:text-emerald-700 transition-colors cursor-pointer"
              >
                Learn More About Deer Deterrent
                <i className="ri-arrow-right-line text-xl ml-2"></i>
              </a>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=White-tailed%20deer%20actively%20eating%20and%20damaging%20residential%20garden%20hostas%20and%20flowering%20shrubs%20in%20suburban%20backyard%2C%20clear%20evidence%20of%20browse%20damage%20on%20plants%2C%20natural%20daylight%20outdoor%20setting%2C%20realistic%20wildlife%20photography%20showing%20deer%20pressure%20on%20landscaping&width=800&height=900&seq=deer-residential-service-001&orientation=portrait"
                alt="Deer damaging residential landscaping"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>

          {/* Recovery Services */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=Thermal%20infrared%20aerial%20drone%20view%20showing%20bright%20heat%20signature%20of%20cattle%20in%20rural%20pasture%20at%20dusk%2C%20warm%20white%20bodies%20clearly%20visible%20against%20dark%20cool%20ground%2C%20professional%20FLIR%20thermal%20imaging%20style%20for%20livestock%20search%20and%20recovery%20operations&width=800&height=900&seq=recovery-service-001&orientation=portrait"
                alt="Thermal drone view of livestock recovery"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-search-eye-line text-6xl text-emerald-800"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4">Animal Recovery Services</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                When livestock, pets, or wildlife go missing, every minute counts. Our thermal imaging drones locate animals quickly—often resolving in hours what could take days on foot.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                We help farmers find missing cattle and calves, pet owners locate lost dogs and cats, and recover wounded deer and wildlife in dense cover.
              </p>
              <ul className="space-y-3 text-gray-700 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>90%+ recovery success rate</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>24/7 emergency response available</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Works day or night with thermal imaging</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Covers 100+ acres per hour</span>
                </li>
              </ul>
              <a
                href="/services/recovery"
                className="inline-flex items-center text-emerald-800 font-semibold hover:text-emerald-700 transition-colors cursor-pointer"
              >
                Learn More About Recovery Services
                <i className="ri-arrow-right-line text-xl ml-2"></i>
              </a>
            </div>
          </div>

          {/* Deer Survey Services */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-contrast-2-line text-6xl text-emerald-800"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4">Deer Survey Services</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Know exactly what you're dealing with. Our thermal imaging drone surveys provide accurate deer population estimates, movement patterns, and activity mapping to support your management decisions.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Perfect for farmers assessing crop damage risk, landowners planning herd management, and properties considering hunting leases or wildlife management programs.
              </p>
              <ul className="space-y-3 text-gray-700 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Accurate population density estimates</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Movement corridors and bedding area identification</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Data-driven herd management decisions</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                  <span>Volume pricing from $5.00/acre down to $1.50/acre</span>
                </li>
              </ul>
              <a
                href="/services/deer-survey"
                className="inline-flex items-center text-emerald-800 font-semibold hover:text-emerald-700 transition-colors cursor-pointer"
              >
                Learn More About Deer Surveys
                <i className="ri-arrow-right-line text-xl ml-2"></i>
              </a>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Thermal%20infrared%20aerial%20survey%20image%20showing%20multiple%20deer%20heat%20signatures%20in%20agricultural%20field%20at%20dusk%2C%20clear%20thermal%20imaging%20with%20bright%20heat%20spots%20indicating%20wildlife%20presence%20and%20movement%20patterns%20near%20crop%20edges%2C%20professional%20wildlife%20monitoring%20photography%20with%20dark%20background%20and%20bright%20thermal%20highlights%20showing%20herd%20distribution&width=800&height=900&seq=deer-survey-service-001&orientation=portrait"
                alt="Thermal deer survey showing population distribution"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Land Management Services */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center">Land Management Services</h2>
          <p className="text-xl text-gray-700 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
            As we work your fields, we see opportunities to help you manage the land around them. These services help reduce crop pressure, protect yields, and create additional value from parcels you already own or lease.
          </p>

          <div className="bg-white p-10 rounded-lg shadow-md">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-16 h-16 flex items-center justify-center mb-6">
                  <i className="ri-tree-line text-6xl text-emerald-800"></i>
                </div>
                <h3 className="text-3xl font-bold mb-4">Managed Woodland Sub-Leasing</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Wooded parcels adjacent to your fields often drive deer pressure and crop damage. We can help you turn those parcels into managed assets that reduce herd pressure and generate income.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  As we spray and survey your operation, we're naturally positioned to identify woodland areas that could be managed for crop protection and revenue generation. Not sure about your field complexity? <a href="/field-complexity" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">Use our estimator tool <i className="ri-arrow-right-line"></i></a>
                </p>
                <div className="bg-emerald-50 p-6 rounded-lg">
                  <h4 className="font-bold text-lg mb-3">What This Means for You:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-xl text-emerald-800 mr-2 mt-1"></i>
                      <span>Reduced deer pressure on field edges</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-xl text-emerald-800 mr-2 mt-1"></i>
                      <span>Additional income from parcels you already own</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-xl text-emerald-800 mr-2 mt-1"></i>
                      <span>Professional management with no extra work on your end</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-xl text-emerald-800 mr-2 mt-1"></i>
                      <span>Better crop protection where it matters most</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Managed%20woodland%20parcel%20adjacent%20to%20agricultural%20farmland%2C%20healthy%20forest%20edge%20bordering%20crop%20field%2C%20clear%20boundary%20showing%20good%20land%20stewardship%2C%20natural%20daylight%20with%20earth%20tones%20and%20green%20forest%20canopy%2C%20professional%20rural%20land%20photography&width=800&height=900&seq=woodland001&orientation=portrait"
                  alt="Managed woodland adjacent to farmland"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Field Intelligence Services */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center">Field Intelligence Services</h2>
        <p className="text-xl text-gray-700 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
          When we're already flying your fields for application, we can layer in survey services that give you actionable data about crop health, stand quality, and wildlife pressure. These aren't fancy reports—they're practical tools farmers already understand and use. <a href="/blog/drone-maps-precision-agriculture" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">Learn more about drone mapping <i className="ri-arrow-right-line"></i></a>
        </p>

        <div className="space-y-12">
          {/* Crop Surveys */}
          <div className="bg-gray-50 p-10 rounded-lg">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-16 h-16 flex items-center justify-center mb-6">
                  <i className="ri-eye-line text-6xl text-emerald-800"></i>
                </div>
                <h3 className="text-3xl font-bold mb-4">Crop Surveys (RGB / NDVI)</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  See your fields the way they really are. These surveys help you spot stand problems, emergence issues, stress patterns, and yield risk areas before they cost you bushels.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  We're already flying your acres for application. Adding a survey layer gives you eyes on every part of your operation without extra flights or wasted time. <a href="/blog/drone-maps-precision-agriculture" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">Read our complete guide to drone mapping <i className="ri-arrow-right-line"></i></a>
                </p>
                <h4 className="font-bold text-lg mb-3">What You Get:</h4>
                <ul className="space-y-3 text-gray-700 mb-6">
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                    <span><strong>Stand counts and emergence checks</strong> – Know where replanting makes sense</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                    <span><strong>Stress detection</strong> – Spot nutrient deficiency, disease, or water stress early</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                    <span><strong>Yield-risk mapping</strong> – Identify areas that need attention before harvest</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                    <span><strong>Drainage and compaction issues</strong> – See patterns you can't spot from the road</span>
                  </li>
                </ul>
                <div className="bg-white p-6 rounded-lg border-2 border-emerald-300">
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Plain talk:</strong> This isn't about fancy technology. It's about knowing where problems are so you can fix them before they cost you money.
                  </p>
                </div>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Aerial%20NDVI%20crop%20health%20survey%20map%20showing%20color-coded%20field%20analysis%20of%20corn%20or%20soybean%20field%2C%20clear%20visualization%20of%20healthy%20green%20zones%20and%20stress%20areas%2C%20professional%20agricultural%20remote%20sensing%20imagery%20with%20natural%20field%20boundaries%20and%20realistic%20data%20overlay&width=800&height=900&seq=ndvi001&orientation=portrait"
                  alt="Crop health survey mapping"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>

          {/* Deer Population Surveys */}
          <div className="bg-gray-50 p-10 rounded-lg">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <img
                  src="https://readdy.ai/api/search-image?query=Thermal%20infrared%20aerial%20survey%20image%20showing%20deer%20herd%20heat%20signatures%20in%20agricultural%20field%20at%20dusk%2C%20clear%20thermal%20imaging%20with%20bright%20heat%20spots%20indicating%20wildlife%20presence%20near%20crop%20edges%2C%20professional%20wildlife%20monitoring%20photography%20with%20dark%20background%20and%20bright%20thermal%20highlights&width=800&height=900&seq=thermal001&orientation=portrait"
                  alt="Thermal deer population survey"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
              <div className="order-1 md:order-2">
                <div className="w-16 h-16 flex items-center justify-center mb-6">
                  <i className="ri-contrast-2-line text-6xl text-emerald-800"></i>
                </div>
                <h3 className="text-3xl font-bold mb-4">Deer Population / Thermal Surveys</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Know what you're dealing with. Seasonal thermal flights help you estimate herd size, track movement patterns, and justify management actions to protect your crop.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  This isn't guesswork. It's data you can use to make decisions about crop protection, leasing, and herd management.
                </p>
                <h4 className="font-bold text-lg mb-3">What You Get:</h4>
                <ul className="space-y-3 text-gray-700 mb-6">
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                    <span><strong>Herd size estimates</strong> – Know how many deer are pressuring your fields</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                    <span><strong>Movement pattern tracking</strong> – See where they're coming from and where they're going</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                    <span><strong>Crop damage justification</strong> – Data to support management decisions and insurance claims</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-1"></i>
                    <span><strong>Seasonal monitoring</strong> – Track changes throughout the growing season</span>
                  </li>
                </ul>
                <div className="bg-white p-6 rounded-lg border-2 border-emerald-300">
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Why it matters:</strong> Deer pressure can cost you 10–20% of your yield in affected areas. Knowing the scope of the problem helps you protect what you've invested in all season.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Combined Value Section */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center">Services That Work Together</h2>
          <p className="text-xl text-gray-700 text-center mb-12 max-w-3xl mx-auto leading-relaxed">
            These aren't separate services—they're parts of a complete land management approach. When you combine precision application with field intelligence and land management, you get better decisions, better timing, and better returns. <a href="/case-studies" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">See real examples <i className="ri-arrow-right-line"></i></a>
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-14 h-14 flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-5xl text-emerald-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Better Decisions</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Field surveys show you where problems are. Application services let you address them on time. Land management reduces pressure before it starts.
              </p>
              <a href="/roi-calculator" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1 text-sm">
                Calculate your potential savings <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-14 h-14 flex items-center justify-center mb-4">
                <i className="ri-time-line text-5xl text-emerald-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Better Timing</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Know what's happening in your fields before it costs you yield. Act when the window is right, not when the ground finally dries out.
              </p>
              <a href="/seasonal-programs" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1 text-sm">
                Explore seasonal programs <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-14 h-14 flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-5xl text-emerald-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Better Returns</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                More bushels per acre. Lower input costs. Additional revenue from managed land. Every service is designed to improve your bottom line.
              </p>
              <a href="/case-studies" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1 text-sm">
                View case studies <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Financing Section */}
      <FinancingBanner context="Crop protection can't wait for budget cycles. Our flexible financing options let you schedule applications when your fields need them — not when the checkbook says so." />

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* CTA Section */}
      <section className="py-20 px-6 max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-6">See If This Fits Your Operation</h2>
        <p className="text-xl text-gray-700 mb-8 leading-relaxed">
          Not every service makes sense for every farm. Let's talk about your operation, your challenges, and what would actually help you protect yield and increase return.
        </p>
        <a
          href="/#talk"
          className="inline-block bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap text-lg"
        >
          Start a Practical Conversation
        </a>
      </section>
    </div>
  );
}
